import type { POChangeLog } from '../../../lib/api';
import type { DeliveryAppealLog } from '../../../lib/api/types/grn-types';

/**
 * CR-4601: how a workflow_status change reads in the audit table.
 * CR-7263: hoisted out of the row renderer so the column's sort key is the
 * same string the cell prints.
 */
export function describeWorkflowAction(newVal: string, oldVal: string): string {
  if (newVal === 'acknowledged') return 'Delivery acknowledged';
  if (newVal === 'disputed') return 'Dispute raised';
  if (newVal === 'resolved') return 'Dispute resolved';
  return `${(oldVal || 'pending review').replace(/_/g, ' ')} → ${(newVal || '').replace(/_/g, ' ')}`;
}

/** Strip the "<status> by <type>." prefix the backend adds to the reason. */
export function cleanWorkflowNote(reason: string | null | undefined): string {
  return (reason || '')
    .replace(/^(GRN\s+)?(acknowledged|disputed|resolved)(\s+by\s+\w+)?[.]?\s*/i, '')
    .trim();
}

export function workflowDescription(log: POChangeLog): string {
  const note = cleanWorkflowNote(log.reason);
  const action = describeWorkflowAction(log.new_value, log.old_value || '');
  return note ? `${action} — ${note}` : action;
}

/**
 * CR-7263: sort keys for the audit columns that are not plain row fields.
 *
 * Company and User both render a fallback chain, and Description is assembled
 * from two fields, so ordering by any single one of them would disagree with
 * what the column shows. The Attachments columns are deliberately absent --
 * they are not sortable, see the headers.
 *
 * Module scope, not inline: the hook memoises on this object's identity, so a
 * fresh literal every render would re-sort on every render.
 */
export const WORKFLOW_SORT_KEYS = {
  changed_by_company: (log: POChangeLog) =>
    log.changed_by_company
    || log.changed_by_details?.companyName
    || (log.company_type ? log.company_type.charAt(0).toUpperCase() + log.company_type.slice(1) : ''),
  changed_by_name: (log: POChangeLog) =>
    log.changed_by_details?.full_name || log.changed_by_details?.email || 'System',
  description: workflowDescription,
} as const;

export const APPEAL_SORT_KEYS = {
  performed_by_name: (log: DeliveryAppealLog) => log.performed_by_name || 'System',
  description: (log: DeliveryAppealLog) =>
    log.notes ? `${log.action} — ${log.notes}` : log.action,
} as const;
